import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../../../context/AuthProvider'
import { useUserRole } from '../../../context/UserRoleProvider'
import { api, apiRequest } from '../../../lib/api'
import { Button } from '../../../components/ui/button'
import {
  ArrowLeft,
  Loader2,
  Trash2,
  Plus,
  Edit3,
  Megaphone,
  Users,
  Lock,
  Check,
  Eye,
  EyeOff,
  Calendar,
} from 'lucide-react'
import MemberSearch from '../../../components/groups/MemberSearch'
import GroupAnnouncementForm from '../../../components/groups/GroupAnnouncementForm'

export default function ParishGroupDetail() {
  const { groupId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { getParishAdminParishId } = useUserRole()

  const [parishId, setParishId] = useState(null)
  const [group, setGroup] = useState(null)
  const [members, setMembers] = useState([])
  const [announcements, setAnnouncements] = useState([])
  const [events, setEvents] = useState([])
  const [eventsLoading, setEventsLoading] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [notFound, setNotFound] = useState(false)
  const [success, setSuccess] = useState(null)

  const [memberError, setMemberError] = useState(null)
  const [addingUserId, setAddingUserId] = useState(null)
  const [removingUserId, setRemovingUserId] = useState(null)

  const [showAnnouncementForm, setShowAnnouncementForm] = useState(false)
  const [postingAnnouncement, setPostingAnnouncement] = useState(false)
  const [announcementError, setAnnouncementError] = useState(null)
  const [editingAnnouncement, setEditingAnnouncement] = useState(null)
  const [deletingAnnouncementId, setDeletingAnnouncementId] = useState(null)

  const resolveParishId = async () => {
    let id = getParishAdminParishId()
    if (!id) {
      const userData = await apiRequest('/api/v1/auth/me')
      const roleDetails = userData?.roleDetails || []
      id = roleDetails.find((r) => r.name === 'parish-admin')?.parishId
    }
    return id
  }

  const flashSuccess = (message) => {
    setSuccess(message)
    setTimeout(() => setSuccess(null), 3000)
  }

  const fetchAll = async () => {
    try {
      setLoading(true)
      setError(null)
      setNotFound(false)

      const [groupData, memberData, announcementData] = await Promise.all([
        api.getGroup(groupId),
        api.getGroupMembers(groupId),
        api.getGroupAnnouncements(groupId),
      ])

      setGroup(groupData)
      setMembers(memberData || [])
      setAnnouncements(announcementData || [])

      const id = await resolveParishId()
      setParishId(id || groupData?.parishId || null)
    } catch (err) {
      console.error('Failed to load group:', err)
      // The API returns 404 (never 403) for a group the caller can't see, so a
      // "not found" message is the only thing we should ever show here.
      if (/not found/i.test(err.message || '')) {
        setNotFound(true)
      } else {
        setError(err.message || 'Failed to load group')
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, user?.id])

  useEffect(() => {
    if (!parishId) return
    setEventsLoading(true)
    apiRequest(`/api/v1/parishes/${parishId}/events?limit=100`)
      .then((data) => {
        const parishEvents = data?.data || data || []
        setEvents(parishEvents.filter((e) => e.groupId === groupId))
      })
      .catch(() => setEvents([]))
      .finally(() => setEventsLoading(false))
  }, [parishId, groupId])

  const handleAddMember = async (candidate) => {
    try {
      setAddingUserId(candidate.id)
      setMemberError(null)
      await api.addGroupMember(groupId, candidate.id)
      const memberData = await api.getGroupMembers(groupId)
      setMembers(memberData || [])
      flashSuccess(`${candidate.name || candidate.email} was added and notified.`)
    } catch (err) {
      console.error('Failed to add member:', err)
      setMemberError(err.message || 'Failed to add member')
    } finally {
      setAddingUserId(null)
    }
  }

  const handleRemoveMember = async (member) => {
    const label = member.user?.name || member.user?.email || 'this member'
    if (!window.confirm(`Remove ${label} from ${group?.name}? They will lose access to this group's announcements.`)) {
      return
    }

    try {
      setRemovingUserId(member.userId)
      setMemberError(null)
      await api.removeGroupMember(groupId, member.userId)
      setMembers((prev) => prev.filter((m) => m.userId !== member.userId))
      flashSuccess(`${label} was removed from the group.`)
    } catch (err) {
      console.error('Failed to remove member:', err)
      setMemberError(err.message || 'Failed to remove member')
    } finally {
      setRemovingUserId(null)
    }
  }

  const closeAnnouncementForm = () => {
    setShowAnnouncementForm(false)
    setEditingAnnouncement(null)
    setAnnouncementError(null)
  }

  const handleNewAnnouncement = () => {
    setEditingAnnouncement(null)
    setAnnouncementError(null)
    setShowAnnouncementForm(true)
  }

  const handleEditAnnouncement = (announcement) => {
    setEditingAnnouncement(announcement)
    setAnnouncementError(null)
    setShowAnnouncementForm(true)
  }

  const handleSaveAnnouncement = async (data) => {
    try {
      setPostingAnnouncement(true)
      setAnnouncementError(null)

      if (editingAnnouncement) {
        await api.updateGroupAnnouncement(groupId, editingAnnouncement.id, data)
      } else {
        await api.createGroupAnnouncement(groupId, data)
      }

      const announcementData = await api.getGroupAnnouncements(groupId)
      setAnnouncements(announcementData || [])
      const wasEditing = Boolean(editingAnnouncement)
      closeAnnouncementForm()
      flashSuccess(
        wasEditing
          ? 'Announcement updated.'
          : 'Announcement posted. Members have been notified.',
      )
    } catch (err) {
      console.error('Failed to save announcement:', err)
      setAnnouncementError(err.message || 'Failed to save announcement')
    } finally {
      setPostingAnnouncement(false)
    }
  }

  const handleDeleteAnnouncement = async (announcement) => {
    if (!window.confirm(`Delete "${announcement.title}"? Members will no longer see it.`)) {
      return
    }

    try {
      setDeletingAnnouncementId(announcement.id)
      setAnnouncementError(null)
      await api.deleteGroupAnnouncement(groupId, announcement.id)
      setAnnouncements((prev) => prev.filter((a) => a.id !== announcement.id))
      if (editingAnnouncement?.id === announcement.id) closeAnnouncementForm()
      flashSuccess('Announcement deleted.')
    } catch (err) {
      console.error('Failed to delete announcement:', err)
      setAnnouncementError(err.message || 'Failed to delete announcement')
    } finally {
      setDeletingAnnouncementId(null)
    }
  }

  const formatDateTime = (dateString) => {
    if (!dateString) return ''
    return new Date(dateString).toLocaleString()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-gold animate-spin" />
      </div>
    )
  }

  if (notFound) {
    return (
      <div className="max-w-2xl mx-auto space-y-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/app/parish/settings/groups')}
          className="text-charcoal hover:text-charcoal-muted"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Groups
        </Button>
        <div className="bg-white border border-surface-border rounded p-8 text-center">
          <p className="text-charcoal-muted">Group not found.</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded p-6">
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    )
  }

  const existingUserIds = members.map((m) => m.userId)
  // The announcement being edited lives in the editor above, not in the list below
  const visibleAnnouncements = editingAnnouncement
    ? announcements.filter((a) => a.id !== editingAnnouncement.id)
    : announcements

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Button
          variant="ghost"
          onClick={() => navigate('/app/parish/settings/groups')}
          className="text-charcoal hover:text-charcoal-muted mb-2"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Groups
        </Button>
        <div className="flex items-center gap-2">
          <Lock className="w-5 h-5 text-gold" />
          <h1 className="text-3xl font-bold font-display text-charcoal">{group.name}</h1>
        </div>
        {group.description && (
          <p className="text-charcoal-muted mt-2 whitespace-pre-wrap">{group.description}</p>
        )}
      </div>

      {success && (
        <div className="bg-green-50 border border-green-200 rounded p-4 flex items-center gap-2">
          <Check className="w-5 h-5 text-green-400" />
          <p className="text-green-700 text-sm">{success}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Events + Announcements */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-surface-border rounded p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-gold" />
                <h2 className="text-xl font-semibold font-display text-charcoal">Events</h2>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/app/parish/events?groupId=${groupId}`)}
              >
                <Plus className="w-4 h-4 mr-2" />
                New Event
              </Button>
            </div>

            {eventsLoading ? (
              <div className="flex items-center gap-2 text-sm text-charcoal-muted">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading events...
              </div>
            ) : events.length === 0 ? (
              <p className="text-charcoal-muted text-sm">
                No events yet. Anything you create here is visible to this group only.
              </p>
            ) : (
              <div className="space-y-3">
                {events.map((event) => (
                  <div
                    key={event.id}
                    className="border border-surface-border rounded p-4 cursor-pointer hover:bg-surface-linen/50 transition-colors"
                    onClick={() => navigate(`/app/parish/events/${event.id}`)}
                  >
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h4 className="text-base font-semibold text-charcoal">{event.title}</h4>
                      {event.isPublished ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-semibold bg-green-50 text-green-700">
                          <Eye className="w-3 h-3" />
                          Published
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-semibold bg-gray-100 text-gray-700">
                          <EyeOff className="w-3 h-3" />
                          Draft
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-charcoal-muted">
                      {formatDateTime(event.startTime)}
                      {event.locationName ? ` · ${event.locationName}` : ''}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white border border-surface-border rounded p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Megaphone className="w-5 h-5 text-gold" />
                <h2 className="text-xl font-semibold font-display text-charcoal">Announcements</h2>
              </div>
              {!showAnnouncementForm && (
                <Button type="button" variant="outline" onClick={handleNewAnnouncement}>
                  <Plus className="w-4 h-4 mr-2" />
                  New Announcement
                </Button>
              )}
            </div>

            {announcementError && (
              <div className="bg-red-50 border border-red-200 rounded p-3 mb-4">
                <p className="text-red-600 text-sm">{announcementError}</p>
              </div>
            )}

            {showAnnouncementForm && (
              <GroupAnnouncementForm
                onSubmit={handleSaveAnnouncement}
                onCancel={closeAnnouncementForm}
                loading={postingAnnouncement}
                memberCount={members.length}
                announcement={editingAnnouncement}
              />
            )}

            {visibleAnnouncements.length === 0 ? (
              // While editing the only announcement, the editor above is the whole
              // list — an "empty" message next to it would just be confusing.
              editingAnnouncement ? null : (
                <p className="text-charcoal-muted text-sm">
                  No announcements yet. Anything you post here reaches this group only.
                </p>
              )
            ) : (
              <div className="space-y-3">
                {visibleAnnouncements.map((announcement) => (
                  <div key={announcement.id} className="border border-surface-border rounded p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h4 className="text-base font-semibold text-charcoal">{announcement.title}</h4>
                          {announcement.isPublished ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-semibold bg-green-50 text-green-700">
                              <Eye className="w-3 h-3" />
                              Posted
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-semibold bg-gray-100 text-gray-700">
                              <EyeOff className="w-3 h-3" />
                              Draft
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-charcoal-muted whitespace-pre-wrap">{announcement.content}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-charcoal-muted">
                          {announcement.author && (
                            <span>By {announcement.author.name || announcement.author.email}</span>
                          )}
                          <span>
                            {announcement.publishedAt
                              ? `Posted ${formatDateTime(announcement.publishedAt)}`
                              : `Created ${formatDateTime(announcement.createdAt)}`}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => handleEditAnnouncement(announcement)}
                          className="p-1.5 text-charcoal-muted hover:text-charcoal transition-colors"
                          title="Edit"
                        >
                          <Edit3 className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteAnnouncement(announcement)}
                          disabled={deletingAnnouncementId === announcement.id}
                          className="p-1.5 text-red-500 hover:text-red-700 transition-colors disabled:opacity-50"
                          title="Delete"
                        >
                          {deletingAnnouncementId === announcement.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Members */}
        <div className="space-y-6">
          <div className="bg-white border border-surface-border rounded p-6">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-5 h-5 text-gold" />
              <h2 className="text-xl font-semibold font-display text-charcoal">
                Members ({members.length})
              </h2>
            </div>

            {memberError && (
              <div className="bg-red-50 border border-red-200 rounded p-3 mb-4">
                <p className="text-red-600 text-sm">{memberError}</p>
              </div>
            )}

            {members.length === 0 ? (
              <p className="text-charcoal-muted text-sm mb-4">
                No members yet. Add people below — they join immediately and are notified.
              </p>
            ) : (
              <div className="border border-surface-border rounded divide-y divide-surface-border mb-4">
                {members.map((member) => (
                  <div key={member.id} className="flex items-center justify-between gap-2 p-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-charcoal truncate">
                        {member.user?.name || member.user?.email}
                      </p>
                      {member.user?.name && (
                        <p className="text-xs text-charcoal-muted truncate">{member.user.email}</p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveMember(member)}
                      disabled={removingUserId === member.userId}
                      className="p-1.5 text-red-500 hover:text-red-700 transition-colors disabled:opacity-50 flex-shrink-0"
                      title="Remove member"
                    >
                      {removingUserId === member.userId ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="pt-4 border-t border-surface-border">
              <h3 className="text-sm font-semibold text-charcoal mb-3">Add a member</h3>
              <MemberSearch
                parishId={parishId}
                existingUserIds={existingUserIds}
                onAdd={handleAddMember}
                addingUserId={addingUserId}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
